import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';

const IfadaDailyReportModal = ({ payments, families, startDate, endDate, onClose }) => {
  const reportRef = useRef(null);

  const getFamilyName = (payment) => {
    // If it's a manual entry (MANUAL-IFADA), extract name from Notes
    if (payment.FamilyID === 'MANUAL-IFADA') {
      // Try to extract student name from Notes
      // Notes format: "Payment for Ifada Order #59 - Manual Entry: StudentName"
      if (payment.Notes) {
        const match = payment.Notes.match(/Manual Entry:\s*(.+)$/);
        if (match && match[1] && match[1] !== 'undefined') {
          return match[1].trim();
        }
      }
      // Fallback: Try to get from ReferenceNumber or just show "Manual Entry"
      return 'Manual Entry';
    }
    
    // Regular family lookup
    const family = families.find(f => f.FamilyID === payment.FamilyID);
    return family ? family.FatherName : payment.FamilyID;
  };

  const formatLBP = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
  };

  // Filter only Ifada payments
  const ifadaPayments = payments.filter(p => p.Module === 'Ifada' && p.Currency === 'LBP');
  const totalAmount = ifadaPayments.reduce((sum, p) => sum + p.Amount, 0);

  const handlePrint = () => {
    const windowPrint = window.open('', '', 'width=900,height=700');
    
    windowPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Ifada Payment Report ${startDate} - ${endDate}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 0;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .header h1 {
              font-size: 32px;
              font-weight: bold;
              color: #111827;
              margin-bottom: 8px;
            }
            .header h2 {
              font-size: 20px;
              font-weight: 600;
              color: #374151;
              margin-bottom: 4px;
            }
            .header p {
              font-size: 14px;
              color: #6b7280;
            }
            .section {
              margin-bottom: 30px;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              color: #1f2937;
              padding: 8px 16px;
              border-radius: 6px;
              margin-bottom: 12px;
              background-color: #f3e8ff;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
            }
            thead tr {
              background-color: #f3f4f6;
              border-bottom: 2px solid #d1d5db;
            }
            th {
              padding: 10px 16px;
              text-align: left;
              font-size: 13px;
              font-weight: 600;
              color: #374151;
            }
            th.align-right {
              text-align: right;
            }
            tbody tr {
              border-bottom: 1px solid #e5e7eb;
            }
            tbody tr:hover {
              background-color: #f9fafb;
            }
            td {
              padding: 10px 16px;
              font-size: 13px;
              color: #1f2937;
            }
            td.align-right {
              text-align: right;
            }
            td.font-semibold {
              font-weight: 600;
            }
            tfoot tr {
              font-weight: bold;
            }
            tfoot tr.total {
              background-color: #f3e8ff;
              border-top: 2px solid #c084fc;
            }
            tfoot td {
              padding: 12px 16px;
            }
            tfoot .total-label {
              text-align: right;
            }
            tfoot .total-amount {
              text-align: right;
              font-size: 16px;
              color: #7c3aed;
            }
            .no-data {
              text-align: center;
              padding: 20px;
              color: #6b7280;
              font-style: italic;
            }
            .grand-total {
              margin-top: 40px;
              padding: 24px;
              background-color: white; 
              color: #000000; 
              border-style: solid !important; 
              border-width: 1px !important;
              border-color: #000000 !important;
              border-radius: 8px;
              box-shadow: 0 0 0 1px #000000; 
            }
            .grand-total-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            }
            .grand-total-label {
              font-size: 24px;
              font-weight: bold;
            }
            .grand-total-amount {
              font-size: 40px;
              font-weight: bold;
              color: #7c3aed;
            }
            .grand-total-details {
              font-size: 13px;
              color: #000000;
            }
            .grand-total-details p {
              margin: 4px 0;
            }
            .signature-section {
              margin-top: 40px;
              padding-top: 24px;
              border-top: 2px solid #d1d5db;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
            }
            .generated-info {
              font-size: 13px;
              color: #6b7280;
            }
            .signature-box {
              width: 250px;
            }
            .signature-line {
              border-top: 2px solid #1f2937;
              margin-bottom: 4px;
            }
            .signature-label {
              font-size: 12px;
              color: #6b7280;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            <h1>Collège des Apôtres</h1>
            <h2>Ifada Payment Report / تقرير مدفوعات الإفادات</h2>
            <p>
              ${startDate === endDate 
                ? `Period: ${new Date(startDate).toLocaleDateString()}`
                : `Period: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
              }
            </p>
          </div>

          <!-- Ifada Payments -->
          <div class="section">
            <h3 class="section-title">Ifada Payments (Lebanese Pounds)</h3>
            ${ifadaPayments.length > 0 ? `
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Receipt #</th>
                    <th>Family</th>
                    <th>Method</th>
                    <th class="align-right">Amount (ل.ل)</th>
                  </tr>
                </thead>
                <tbody>
                  ${ifadaPayments.map(payment => `
                    <tr>
                      <td>${new Date(payment.PaymentDate).toLocaleDateString()}</td>
                      <td>#${payment.PaymentID}</td>
                      <td>${payment.FamilyID === 'MANUAL-IFADA' ? getFamilyName(payment) : payment.FamilyID + ' - ' + getFamilyName(payment)}</td>
                      <td>${payment.PaymentMethod}</td>
                      <td class="align-right font-semibold">${formatLBP(payment.Amount)}</td>
                    </tr>
                  `).join('')}
                </tbody>
                <tfoot>
                  <tr class="total">
                    <td colspan="4" class="total-label">Total:</td>
                    <td class="total-amount">${formatLBP(totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            ` : '<p class="no-data">No Ifada payments in this period</p>'}
          </div>

          <!-- Grand Total -->
          <div class="grand-total">
            <div class="grand-total-row">
              <span class="grand-total-label">GRAND TOTAL:</span>
              <span class="grand-total-amount">${formatLBP(totalAmount)}</span>
            </div>
            <div class="grand-total-details">
              <p>Total Transactions: ${ifadaPayments.length}</p>
              <p>Currency: Lebanese Pounds (LBP)</p>
            </div>
          </div>

          <!-- Signature Section -->
          <div class="signature-section">
            <div class="generated-info">
              <p>Generated: ${new Date().toLocaleString()}</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="signature-label">Authorized Signature</p>
            </div>
          </div>
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    
    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-800">Ifada Daily Payment Report</h3>
          <div className="flex gap-2">
            <button 
              onClick={handlePrint} 
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
            >
              <Printer size={18} />
              Print
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
        </div>

        <div ref={reportRef} className="p-8">
          {/* Preview content with Tailwind classes */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Collège des Apôtres</h1>
            <h2 className="text-xl font-semibold text-gray-700 mt-2">Ifada Payment Report / تقرير مدفوعات الإفادات</h2>
            <p className="text-gray-600 mt-1">
              {startDate === endDate 
                ? `Period: ${new Date(startDate).toLocaleDateString()}`
                : `Period: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
              }
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 bg-purple-100 px-4 py-2 rounded">Ifada Payments (Lebanese Pounds)</h3>
            {ifadaPayments.length > 0 ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                    <th className="px-4 py-2 text-right text-sm font-semibold">Amount (ل.ل)</th>
                  </tr>
                </thead>
                <tbody>
                  {ifadaPayments.map(payment => (
                    <tr key={payment.PaymentID} className="border-b border-gray-200">
                      <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                      <td className="px-4 py-2 text-sm">
                        {payment.FamilyID === 'MANUAL-IFADA' 
                          ? getFamilyName(payment)
                          : `${payment.FamilyID} - ${getFamilyName(payment)}`
                        }
                      </td>
                      <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                      <td className="px-4 py-2 text-sm text-right font-semibold">{formatLBP(payment.Amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-purple-50 font-bold border-t-2 border-purple-300">
                    <td colSpan="4" className="px-4 py-3 text-right">Total:</td>
                    <td className="px-4 py-3 text-right text-purple-700 text-lg">{formatLBP(totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="text-gray-500 italic text-center py-4">No Ifada payments in this period</p>
            )}
          </div>

          <div className="mt-8 p-6 bg-white text-black border-2 border-gray-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold">GRAND TOTAL:</span>
              <span className="text-4xl font-bold text-purple-600">{formatLBP(totalAmount)}</span>
            </div>
            <div className="mt-4 text-sm">
              <p>Total Transactions: {ifadaPayments.length}</p>
              <p>Currency: Lebanese Pounds (LBP)</p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t-2 border-gray-300 flex justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-4">Generated: {new Date().toLocaleString()}</p>
            </div>
            <div className="w-64">
              <div className="border-t-2 border-gray-800 mb-1"></div>
              <p className="text-xs text-gray-600 text-center">Authorized Signature</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IfadaDailyReportModal;